import { Injectable } from '@angular/core';

const PBKDF2_ITERATIONS = 100_000;
const SALT_LENGTH = 16;
const IV_LENGTH = 12;

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  async encryptText(plainText: string, password: string): Promise<string> {
    const cryptoApi = this.getCrypto();
    const salt = this.createRandomBytes(SALT_LENGTH);
    const iv = this.createRandomBytes(IV_LENGTH);
    const key = await this.deriveKey(password, salt);
    const encodedText = new TextEncoder().encode(plainText);
    const encryptedBuffer = await cryptoApi.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encodedText,
    );

    const encryptedBytes = new Uint8Array(encryptedBuffer);
    const combined = new Uint8Array(salt.length + iv.length + encryptedBytes.length);

    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(encryptedBytes, salt.length + iv.length);

    return this.bufferToBase64(combined);
  }

  async decryptText(encryptedPayload: string, password: string): Promise<string> {
    try {
      const combined = this.base64ToBuffer(encryptedPayload);

      if (combined.length <= SALT_LENGTH + IV_LENGTH) {
        throw new Error('Encrypted payload is too short.');
      }

      const salt = combined.slice(0, SALT_LENGTH);
      const iv = combined.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
      const encryptedBytes = combined.slice(SALT_LENGTH + IV_LENGTH);
      const key = await this.deriveKey(password, salt);
      const decryptedBuffer = await this.getCrypto().subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encryptedBytes,
      );

      return new TextDecoder().decode(decryptedBuffer);
    } catch {
      throw new Error('Hibas titkositasi jelszo, PIN vagy serult titkositott adat.');
    }
  }

  private async deriveKey(password: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
    const cryptoApi = this.getCrypto();
    const passwordBytes = new TextEncoder().encode(password);
    const keyMaterial = await cryptoApi.subtle.importKey(
      'raw',
      passwordBytes,
      { name: 'PBKDF2' },
      false,
      ['deriveKey'],
    );

    return cryptoApi.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  private bufferToBase64(bytes: Uint8Array): string {
    let binaryString = '';

    for (const byte of bytes) {
      binaryString += String.fromCharCode(byte);
    }

    return btoa(binaryString);
  }

  private base64ToBuffer(base64Value: string): Uint8Array {
    const binaryString = atob(base64Value);
    const bytes = new Uint8Array(binaryString.length);

    for (let index = 0; index < binaryString.length; index += 1) {
      bytes[index] = binaryString.charCodeAt(index);
    }

    return bytes;
  }

  private getCrypto(): Crypto {
    if (!globalThis.crypto?.subtle) {
      throw new Error('Web Crypto API is not available in this environment.');
    }

    return globalThis.crypto;
  }

  private createRandomBytes(length: number): Uint8Array<ArrayBuffer> {
    const target = new Uint8Array(new ArrayBuffer(length));

    this.getCrypto().getRandomValues(target);

    return target;
  }
}